import React from 'react';
import { Plus, Trash2, ChevronRight, Layers, GripVertical, Copy, MoreHorizontal } from 'lucide-react';
import { FormSchema } from '../types';
import { iconButton } from '../ui';
import { builderFieldCount } from './helpers';
import { SectionSummary } from './panelParts';

type StepsSectionProps = {
  schema: FormSchema;
  stepIndex: number;
  dragStepIndex: number | undefined;
  setDragStepIndex: (index: number | undefined) => void;
  stepMenuIndex: number | undefined;
  setStepMenuIndex: React.Dispatch<React.SetStateAction<number | undefined>>;
  onSelectStep: (index: number) => void;
  onAddStep: () => void;
  onMoveStep: (from: number, to: number) => void;
  onDuplicateStep: (index: number) => void;
  onDeleteStep: (index: number) => void;
};

export default function StepsSection({
  schema,
  stepIndex,
  dragStepIndex,
  setDragStepIndex,
  stepMenuIndex,
  setStepMenuIndex,
  onSelectStep,
  onAddStep,
  onMoveStep,
  onDuplicateStep,
  onDeleteStep,
}: StepsSectionProps) {
  return (
          <details data-tour="editor-steps" className="group rounded-xl border border-violet-200 border-l-4 border-l-violet-500 bg-violet-50/60 shadow-sm">
            <SectionSummary
              icon={<Layers className="h-3.5 w-3.5 text-violet-600" />}
              title="Steps"
              description={`${schema.steps.length} step${schema.steps.length === 1 ? '' : 's'}`}
              action={
                <button
                  type="button"
                  className={iconButton}
                  title="Add step"
                  aria-label="Add step"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAddStep(); }}
                >
                  <Plus className="h-4 w-4" />
                </button>
              }
            />
            <div className="space-y-1 px-3">
              {schema.steps.map((s, i) => (
                <div
                  key={s.id}
                  draggable
                  onDragStart={() => setDragStepIndex(i)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => {
                    if (dragStepIndex !== undefined) onMoveStep(dragStepIndex, i);
                    setDragStepIndex(undefined);
                  }}
                  onDragEnd={() => setDragStepIndex(undefined)}
                  className={`group flex items-center gap-1 rounded-lg border px-1.5 py-1.5 transition ${i === stepIndex ? 'border-primary-200 bg-primary-50' : 'border-transparent hover:border-slate-200 hover:bg-slate-50'} ${dragStepIndex === i ? 'opacity-40' : ''}`}
                >
                  <div className="cursor-grab px-0.5 text-slate-300 hover:text-slate-500" title="Drag to reorder steps">
                    <GripVertical className="h-3.5 w-3.5" />
                  </div>
                  <button type="button" onClick={() => { onSelectStep(i); }} className={`flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-0.5 text-left ${i === stepIndex ? 'text-primary-800' : 'text-slate-600'}`}>
                    <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-md text-[10px] font-semibold ${i === stepIndex ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-500'}`}>{i + 1}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[12px] font-medium">{s.title || `Step ${i + 1}`}</span>
                      <span className="mt-0.5 block text-[10px] text-slate-400">{builderFieldCount(s)} field{builderFieldCount(s) === 1 ? '' : 's'}</span>
                    </span>
                    {i === stepIndex && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-primary-500" />}
                  </button>
                  <div className="relative shrink-0">
                    <button
                      type="button"
                      className={iconButton}
                      title="Step actions"
                      aria-label={`Actions for ${s.title || `Step ${i + 1}`}`}
                      aria-haspopup="menu"
                      aria-expanded={stepMenuIndex === i}
                      onClick={(e) => {
                        e.stopPropagation();
                        setStepMenuIndex((current) => current === i ? undefined : i);
                      }}
                    >
                      <MoreHorizontal className="h-3.5 w-3.5" />
                    </button>
                    {stepMenuIndex === i && (
                      <div
                        role="menu"
                        className="absolute right-0 top-full z-30 mt-1 min-w-[140px] rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          role="menuitem"
                          className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[11px] font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                          onClick={() => {
                            onDuplicateStep(i);
                            setStepMenuIndex(undefined);
                          }}
                        >
                          <Copy className="h-3.5 w-3.5" />
                          Duplicate step
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          disabled={schema.steps.length <= 1}
                          className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[11px] font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                          title={schema.steps.length <= 1 ? 'At least one step is required' : 'Delete step'}
                          onClick={() => {
                            onDeleteStep(i);
                            setStepMenuIndex(undefined);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete step
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button
              className="mx-3 mb-3 mt-2 flex h-8 w-[calc(100%-1.5rem)] items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-300 text-[11px] font-medium text-slate-500 transition hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
              onClick={onAddStep}
            >
              <Plus className="h-3.5 w-3.5" /> Add step
            </button>
          </details>
  );
}
